import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useState, useMemo, useRef, useCallback } from 'react';
import { Stack, Typography, Box, Button } from '@mui/material';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';

import {
  CrossEntityDateCrudDataGrid,
  BaseEntity,
  BaseData,
  ValueWithEntityReference,
  CrossEntityDateCrudDataGridHandle,
  CrossEntityTableDataItem,
  ValuePropConfig,
  reconcileColumnOrder,
} from '../../CrossEntityDateCrudDataGrid';
import dayjs from 'dayjs';
import {
  DiagnosisRelevantVariablesReadDTO,
  DiagnosisReadDTO,
  VariablesDataReadDTO,
  VariablesDataCreateDTO,
} from '../../../generated/api-client';
import { DiagnosisStepHandle } from './Step.types';
import { apiClient, axiosInstance } from '../../../services/apiClient';
import { getAxiosErrorDetail } from '../../../utils/axiosErrorDetail';
import { toYyyyMmDd } from '../../../utils/date.utils';
import { EditRelevantVariableDialog } from './EditRelevantVariableDialog';
import { GridRowId } from '@mui/x-data-grid';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { ColumnMappingDialog, type ColumnMappingField } from '../../common/ColumnMappingDialog';
import { ActionSpacer } from '../../common';
import {
  useBatchSaveVariablesData,
  useDiagnosisRelevantVariablesByDiagnosisId,
} from '../../../hooks';
import { formatUnitOfMeasurement } from '../../../utils/unitOfMeasurementFormatter';
import {
  buildReferencePeriodYearlyValues,
  type SourceValueItem,
} from '../../../utils/referencePeriodYearlyValues';

interface DiagnosisRelevantVariablesStepProps {
  diagnosisId?: string;
  diagnosis?: DiagnosisReadDTO | null;
}

export const DiagnosisRelevantVariablesStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisRelevantVariablesStepProps>(
    ({ diagnosisId, diagnosis }, ref) => {
      const { readOnly } = useDiagnosisPermission();
      const queryClient = useQueryClient();
      const crossEntityGridRef = useRef<CrossEntityDateCrudDataGridHandle>(null);
      const crossEntityGridRefAnnual = useRef<CrossEntityDateCrudDataGridHandle>(null);
      const crossEntityGridRefPeriodoRiferimento = useRef<CrossEntityDateCrudDataGridHandle>(null);

      // Ordine colonne (ID variabili): condiviso fra la griglia mensile e le due tabelle annue.
      // Essendo passato come prop "controlled" a tutte e tre le griglie, lo spostamento di una
      // colonna (possibile solo dalla griglia mensile) si riflette subito anche nelle tabelle
      // annue, prima ancora del salvataggio.
      const [entityOrder, setEntityOrder] = useState<string[]>([]);

      const [editDialogOpen, setEditDialogOpen] = useState(false);
      const [selectedVariable, setSelectedVariable] =
        useState<DiagnosisRelevantVariablesReadDTO | null>(null);
      const [deleteDialogState, setDeleteDialogState] = useState<{
        open: boolean;
        id: string | null;
      }>({ open: false, id: null });
      const [pasteMappingDialog, setPasteMappingDialog] = useState<{
        open: boolean;
        clipboardData: string[][];
      }>({ open: false, clipboardData: [] });

      const { data: variables = [], isLoading } = useDiagnosisRelevantVariablesByDiagnosisId(
        diagnosisId!
      );

      // Chiave stabile per sincronizzare l'effetto di riconciliazione solo quando cambia
      // davvero l'elenco delle variabili (non ad ogni refetch con stesso contenuto).
      const variablesIdsKey = useMemo(
        () => variables.map(v => v.id).filter(Boolean).join('|'),
        [variables]
      );

      // Riconcilia l'ordine colonne quando cambia l'elenco delle variabili (nuova variabile
      // creata, variabile eliminata), mantenendo l'ordine scelto dall'utente per quelle
      // ancora presenti. Il "default order" è l'ordine attuale lato backend (campo `index`).
      React.useEffect(() => {
        const defaultOrder = variables.map(v => v.id!).filter(Boolean);
        setEntityOrder(prev => {
          const reconciled = reconcileColumnOrder(prev, defaultOrder);
          const unchanged =
            reconciled.length === prev.length && reconciled.every((id, i) => id === prev[i]);
          return unchanged ? prev : reconciled;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [variablesIdsKey]);

      // Field names (`entity-{id}`) per le prop "controlled" delle 3 griglie
      const columnOrderFields = useMemo(() => entityOrder.map(id => `entity-${id}`), [entityOrder]);
      const handleColumnOrderChange = useCallback((fields: string[]) => {
        setEntityOrder(fields.map(f => f.replace('entity-', '')));
      }, []);

      // Carica tutti i VariablesData per tutte le variabili della diagnosi
      const { data: allVariablesData = [], isLoading: isLoadingVariablesData } = useQuery<
        VariablesDataReadDTO[]
      >(
        `all-variables-data-${diagnosisId}`,
        async () => {
          if (!variables.length) return [];
          // Carica i dati per ogni variabile
          const allData: VariablesDataReadDTO[] = [];
          for (const variable of variables) {
            if (variable.id) {
              const data =
                await apiClient.search_by_variable_id_variables_data_search_by_variable_id__variable_id__get(
                  variable.id
                );
              allData.push(...data);
            }
          }
          return allData;
        },
        { enabled: !!diagnosisId && variables.length > 0 }
      );

      const deleteMutation = useMutation(
        async (id: string) => {
          await apiClient.delete_item_diagnosis_relevant_variables__item_id__delete(id);
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(`diagnosis-relevant-variables-${diagnosisId}`);
            queryClient.invalidateQueries(`all-variables-data-${diagnosisId}`);
            toast.success('Variabile pertinente eliminata con successo');
            setDeleteDialogState({ open: false, id: null });
          },
          onError: (error: any) => {
            toast.error(error?.message || 'Eliminazione della variabile pertinente non riuscita');
          },
        }
      );

      // Batch save mutation for variables data
      const batchSaveVariablesData = useBatchSaveVariablesData();

      // Mutation for updating variable order
      const updateOrderMutation = useMutation(
        async (variableIds: string[]) => {
          try {
            const { data } = await axiosInstance.post(
              '/diagnosis-relevant-variables/update_order',
              { variable_ids: variableIds }
            );
            return data;
          } catch (e: unknown) {
            throw new Error(getAxiosErrorDetail(e, "Errore nel salvataggio dell'ordinamento"));
          }
        },
        {
          onSuccess: () => {
            // L'ordine locale (entityOrder) è già quello inviato: non serve risincronizzarlo qui,
            // basta invalidare la query per riflettere l'`index` aggiornato lato backend.
            queryClient.invalidateQueries(`diagnosis-relevant-variables-${diagnosisId}`);
            toast.success('Ordinamento variabili salvato con successo');
          },
          onError: (error: any) => {
            toast.error(
              error?.message || "Errore nel salvataggio dell'ordinamento delle variabili"
            );
          },
        }
      );

      const batchSaveMutation = useMutation(
        async (itemsToSave: Array<VariablesDataCreateDTO & { id?: string }>) => {
          // Salva tutti i dati modificati in una sola chiamata batch
          if (itemsToSave.length > 0) {
            await batchSaveVariablesData.mutateAsync(itemsToSave);
          }
        },
        {
          onSuccess: results => {
            queryClient.invalidateQueries(`all-variables-data-${diagnosisId}`);
            toast.success('Dati variabili pertinenti salvati con successo');
          },
          onError: (error: any) => {
            toast.error(error?.message || 'Errore nel salvataggio dei dati variabili pertinenti');
          },
        }
      );

      React.useImperativeHandle(ref, () => ({
        save: async () => {
          if (!crossEntityGridRef.current) {
            return { success: true };
          }

          // IMPORTANTE: leggi prima i dati della tabella e solo dopo salva l'ordinamento.
          // `updateOrderMutation` invalida la query delle variabili: se l'ordine venisse salvato
          // per primo, il refetch potrebbe ricalcolare `sortedVariables`/le righe della griglia
          // prima che i dati non ancora salvati vengano letti, con il rischio di perderli
          // (causa principale della sparizione dei dati osservata dopo lo spostamento colonna).
          const tableData = crossEntityGridRef.current.getTableData();

          // Save order if changed (compare with original backend order)
          const originalOrder = variables.map(v => v.id!).filter(Boolean);
          const orderChanged =
            entityOrder.length === originalOrder.length &&
            entityOrder.some((id, index) => id !== originalOrder[index]);

          if (orderChanged) {
            try {
              await updateOrderMutation.mutateAsync(entityOrder);
            } catch (error) {
              // Error handled by mutation's onError
              return { success: false };
            }
          }

          if (tableData.length === 0) {
            return { success: true };
          }

          // Create a map of original values for comparison
          // Key: `${variable_id}-${month}`, Value: original value
          const originalValuesMap = new Map<string, number | null | undefined>();
          allVariablesData.forEach(data => {
            if (data.variable_id && data.month) {
              const key = `${data.variable_id}-${dayjs(data.month).format('YYYY-MM')}`;
              originalValuesMap.set(key, data.value ?? (null as any));
            }
          });

          // Raccogli solo i dati modificati
          const itemsToSave: Array<VariablesDataCreateDTO & { id?: string }> = [];

          for (const item of tableData) {
            // Normalize new value: convert to number or null
            const newValue =
              item.value !== null && item.value !== '' && item.value !== undefined
                ? Number(item.value)
                : null;

            // If this is an existing record, compare with original value
            if (item.valueId) {
              const key = `${item.entityId}-${dayjs(item.month).format('YYYY-MM')}`;
              const originalValue = originalValuesMap.get(key);

              // Normalize original value for comparison
              const normalizedOriginal =
                originalValue !== null && originalValue !== undefined
                  ? Number(originalValue)
                  : null;

              // Skip update if value hasn't changed (compare normalized values)
              if (normalizedOriginal === newValue) {
                continue; // Skip this item, no changes
              }
            }

            const createDto = new VariablesDataCreateDTO({
              variable_id: item.entityId,
              month: toYyyyMmDd(item.month),
              value: newValue !== null ? newValue : (undefined as any),
            });

            const dtoWithId = createDto as VariablesDataCreateDTO & { id?: string };
            if (item.valueId) {
              dtoWithId.id = item.valueId;
            }

            itemsToSave.push(dtoWithId);
          }

          // Perform batch upsert
          if (itemsToSave.length > 0) {
            await batchSaveMutation.mutateAsync(itemsToSave);
          }

          return { success: true };
        },
        add: async () => {
          if (!diagnosisId) {
            toast.error('Salva prima i dati principali della diagnosi.');
            return;
          }
          // Apri il dialog di modifica con una nuova variabile
          setSelectedVariable(null);
          setEditDialogOpen(true);
        },
        actions: ['save', 'add'],
      }));

      const handleEditClick = (id: GridRowId) => {
        const variable = variables.find(v => v.id === id);
        if (variable) {
          setSelectedVariable(variable);
          setEditDialogOpen(true);
        }
      };

      const handleDeleteClick = (id: GridRowId) => {
        setDeleteDialogState({ open: true, id: id as string });
      };

      const handleCloseDeleteDialog = () => {
        setDeleteDialogState({ open: false, id: null });
      };

      const handleConfirmDelete = () => {
        if (deleteDialogState.id) {
          deleteMutation.mutate(deleteDialogState.id);
        }
      };

      const handlePaste = useCallback(async () => {
        if (!diagnosisId) {
          toast.error('Salva prima i dati principali della diagnosi.');
          return;
        }
        try {
          const text = await navigator.clipboard.readText();
          let lines = text.split(/\r?\n/);
          if (lines.length > 0 && lines[lines.length - 1] === '') {
            lines = lines.slice(0, -1);
          }
          if (lines.length === 0) {
            toast.error('Nessun dato trovato nel clipboard');
            return;
          }
          const clipboardData: string[][] = lines.map(line =>
            line.split('\t').map(col => col.trim())
          );
          setPasteMappingDialog({ open: true, clipboardData });
        } catch (error) {
          console.error('Errore durante il paste:', error);
          toast.error('Errore durante la lettura del clipboard');
        }
      }, [diagnosisId]);

      const handleImportWithMapping = useCallback(
        async (mapping: Map<number, string>) => {
          if (!diagnosisId) {
            toast.error('Salva prima i dati principali della diagnosi.');
            return;
          }
          if (mapping.size === 0) {
            toast.error('Mappa almeno una riga a un campo');
            return;
          }
          const newVariables: any[] = [];
          const maxRows = pasteMappingDialog.clipboardData.length;
          const maxCols = Math.max(...pasteMappingDialog.clipboardData.map(row => row.length));

          const maxIndex =
            variables.length > 0
              ? Math.max(...variables.map(v => (v as any).index ?? 0))
              : 0;

          for (let colIdx = 0; colIdx < maxCols; colIdx++) {
            const variableData: any = {
              diagnosis_id: diagnosisId,
              index: maxIndex + colIdx + 1,
            };
            mapping.forEach((fieldName, rowIndex) => {
              if (rowIndex >= maxRows) return;
              const value = pasteMappingDialog.clipboardData[rowIndex]?.[colIdx]?.trim();
              if (value === undefined || value === '') return;

              if (fieldName === 'variable') {
                variableData[fieldName] = value;
              } else if (fieldName === 'unit_of_measurement') {
                variableData[fieldName] = value;
              } else if (fieldName === 'table_code_index') {
                variableData[fieldName] = value;
              } else if (fieldName === 'hierarchy') {
                const num = parseInt(value, 10);
                if (!isNaN(num)) variableData[fieldName] = num;
              } else if (fieldName === 'type') {
                variableData[fieldName] = value;
              } else if (fieldName === 'notes') {
                variableData[fieldName] = value;
              }
            });

            if (!variableData.table_code_index) continue;
            newVariables.push(variableData);
          }

          if (newVariables.length === 0) {
            toast.error('Nessuna variabile valida trovata (serve almeno ID variabile / strumento)');
            return;
          }

          try {
            await Promise.all(
              newVariables.map(data =>
                apiClient.create_item_diagnosis_relevant_variables__post(data as any)
              )
            );
            queryClient.invalidateQueries(`diagnosis-relevant-variables-${diagnosisId}`);
            queryClient.invalidateQueries(`all-variables-data-${diagnosisId}`);
            setPasteMappingDialog({ open: false, clipboardData: [] });
            toast.success(`${newVariables.length} variabile/i aggiunta/e dalla clipboard`);
          } catch (error: any) {
            toast.error(error?.message || 'Errore durante la creazione delle variabili');
          }
        },
        [diagnosisId, variables, pasteMappingDialog.clipboardData, queryClient]
      );

      const variableFields: ColumnMappingField[] = useMemo(
        () => [
          { field: 'variable', headerName: 'Variabile', width: 180 },
          { field: 'unit_of_measurement', headerName: 'UdM', width: 100 },
          { field: 'table_code_index', headerName: 'ID variabile / strumento', width: 200 },
          { field: 'hierarchy', headerName: 'Gerarchia', width: 100 },
          { field: 'type', headerName: 'Tipologia', width: 120 },
          { field: 'notes', headerName: 'Note', width: 200 },
        ],
        []
      );

      // Prepara i dati per la tabella cross-entity
      const crossEntityValues: ValueWithEntityReference<VariablesDataReadDTO>[] =
        React.useMemo(() => {
          return allVariablesData.map(data => ({
            value: data,
            entityId: data.variable_id ?? '',
            month: data.month ?? new Date(),
          }));
        }, [allVariablesData]);

      // Totali annui per variabile: somma dei valori mensili per anno (una sola colonna "Totale annuo")
      const variableYearlyValues = useMemo((): ValueWithEntityReference<{ value: number }>[] => {
        if (!crossEntityValues.length) return [];
        const fromYear = diagnosis?.data_generated_from
          ? dayjs(diagnosis.data_generated_from).year()
          : new Date().getFullYear();
        const toYear = diagnosis?.data_generated_to
          ? dayjs(diagnosis.data_generated_to).year()
          : fromYear;
        const years = new Set<number>();
        for (let y = fromYear; y <= toYear; y++) years.add(y);

        const sumByEntityYear = new Map<string, number>();
        crossEntityValues.forEach(item => {
          const year = dayjs(item.month).year();
          if (!years.has(year)) return;
          const key = `${item.entityId}-${year}`;
          const num = Number((item.value as any)?.value ?? 0);
          sumByEntityYear.set(key, (sumByEntityYear.get(key) ?? 0) + num);
        });

        const result: ValueWithEntityReference<{ value: number }>[] = [];
        variables.forEach(v => {
          const eid = v.id;
          if (!eid) return;
          years.forEach(year => {
            const key = `${eid}-${year}`;
            const sum = sumByEntityYear.get(key) ?? 0;
            result.push({
              entityId: eid,
              date: new Date(year, 0, 1),
              value: { value: sum },
            });
          });
        });
        return result;
      }, [
        crossEntityValues,
        variables,
        diagnosis?.data_generated_from,
        diagnosis?.data_generated_to,
      ]);

      // Totali per periodo di riferimento: solo mesi in [reference_period_from, reference_period_to]
      const variableReferencePeriodYearlyValues = useMemo((): ValueWithEntityReference<{
        value: number;
      }>[] => {
        if (!diagnosis?.reference_period_from || !diagnosis?.reference_period_to) return [];
        const entityIds = variables.map(v => v.id).filter(Boolean) as string[];
        return buildReferencePeriodYearlyValues({
          sourceValues: crossEntityValues as SourceValueItem[],
          periodFrom: diagnosis.reference_period_from,
          periodTo: diagnosis.reference_period_to,
          entityIds,
          getValueToSum: item => Number((item.value as any)?.value ?? 0),
          buildValue: (_entityId, _year, sum, _count, _ctx) => ({ value: sum }),
        });
      }, [
        crossEntityValues,
        variables,
        diagnosis?.reference_period_from,
        diagnosis?.reference_period_to,
      ]);

      const variableYearlyValuesProps: ValuePropConfig<{ value: number }>[] = useMemo(
        () => [
          {
            field: 'value',
            label: 'Totale annuo',
            isPrimary: true,
            editable: false,
            getValue: v => (v != null ? Number((v as any).value) : null),
            setValue: v => v ?? { value: 0 },
          },
        ],
        []
      );

      // Posizione assoluta scroll orizzontale (valore unico per tutte e tre le tabelle)
      const [horizontalScrollLeft, setHorizontalScrollLeft] = React.useState(0);
      const setScrollPos = React.useCallback((pos: number) => {
        setHorizontalScrollLeft(prev => (prev === pos ? prev : pos));
      }, []);

      // Ordine colonne: stesso per la griglia mensile e le due tabelle annue, pilotato da
      // `entityOrder` (stato condiviso, aggiornato live quando l'utente sposta una colonna).
      const sortedVariables = useMemo(() => {
        if (!entityOrder.length) return variables;
        const byId = new Map(variables.map(v => [v.id, v]));
        const ordered = entityOrder
          .map(id => byId.get(id))
          .filter((v): v is DiagnosisRelevantVariablesReadDTO => !!v);
        // Se l'ordine non è ancora stato riconciliato con l'elenco corrente (es. nel render
        // immediatamente successivo all'aggiunta/eliminazione di una variabile), usa `variables`
        // per non perdere colonne in visualizzazione: l'effetto di riconciliazione lo sistema
        // al render successivo.
        return ordered.length === variables.length ? ordered : variables;
      }, [variables, entityOrder]);

      // Callback quando una cella viene modificata
      const handleCellEditStop = () => {
        // Cell edit stopped
      };

      // Header personalizzato per la prima colonna (collapsed state)
      const dateColumnHeader = React.useMemo(() => {
        return (
          <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
            <Typography variant="caption" fontWeight={600}>
              Informazioni variabili
            </Typography>
          </Stack>
        );
      }, []);

      // Header personalizzato per la prima colonna (expanded state)
      const dateColumnHeaderExpanded = React.useMemo(() => {
        return (
          <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
            <Typography variant="caption" fontWeight={600} sx={{ pt: '0.2rem' }}>
              Informazioni variabili
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              ID contatore cliente
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Variabile
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Unità di misura
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Tipologia
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Gerarchia
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Note
            </Typography>
            <Box sx={{ height: '24px' }}></Box>
          </Stack>
        );
      }, []);

      // Header personalizzato per le colonne delle variabili (collapsed state)
      const renderVariableHeader = React.useCallback(
        (
          variable: DiagnosisRelevantVariablesReadDTO,
          monthlyValues: { month: Date; value: number | string | null }[]
        ) => {
          return (
            <Box sx={{ my: '4px' }}>
              <Typography
                variant="caption"
                fontWeight={600}
                noWrap
                title={variable.table_code_index}
                sx={{ textAlign: 'center' }}
              >
                {variable.variable || ''}
                {' - '}
                {variable.table_code_index || ''}{' '}
                {variable.unit_of_measurement &&
                  `[${formatUnitOfMeasurement(variable.unit_of_measurement)}]`}
              </Typography>
            </Box>
          );
        },
        []
      );

      // Header personalizzato per le colonne delle variabili (expanded state)
      const renderVariableHeaderExpanded = React.useCallback(
        (
          variable: DiagnosisRelevantVariablesReadDTO,
          monthlyValues: { month: Date; value: number | string | null }[]
        ) => {
          return (
            <Stack spacing={0.5} sx={{ fontSize: '0.7rem', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ cursor: 'pointer' }} onClick={() => handleEditClick(variable.id as any)}>
                  <EditIcon fontSize="small" />
                </Box>
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleDeleteClick(variable.id as any)}
                >
                  <DeleteIcon fontSize="small" />
                </Box>
              </Box>
              <Typography variant="caption" fontWeight={600} noWrap>
                {variable.table_code_index || '-'}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {(variable as any).variable || '-'}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {formatUnitOfMeasurement(variable.unit_of_measurement || '-')}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {variable.type || '-'}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {variable.hierarchy || '-'}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap title={variable.notes}>
                {variable.notes || '-'}
              </Typography>
            </Stack>
          );
        },
        [handleEditClick, handleDeleteClick]
      );

      if (!diagnosisId) {
        return (
          <Typography color="text.secondary">
            Salva le informazioni principali della diagnosi per gestire le variabili pertinenti.
          </Typography>
        );
      }

      const hasGeneratedPeriod = diagnosis?.data_generated_from && diagnosis?.data_generated_to;

      if (!hasGeneratedPeriod) {
        return <Box sx={{ mt: 2 }}>Selezionare un periodo dati generali</Box>;
      }

      return (
        <>
          <Stack spacing={2} sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Variabili pertinenti</Typography>
              {hasGeneratedPeriod && (
                <Button onClick={handlePaste} variant="contained" startIcon={<ContentPasteIcon />}>
                  Incolla variabili
                </Button>
              )}
            </Stack>
            {hasGeneratedPeriod && variables && (
              <Stack spacing={3} sx={{ mt: 3 }}>
                <Box sx={{ width: '100%' }}>
                  <CrossEntityDateCrudDataGrid
                    ref={crossEntityGridRef}
                    periodFrom={diagnosis.data_generated_from!}
                    periodTo={diagnosis.data_generated_to!}
                    entities={sortedVariables}
                    values={crossEntityValues}
                    getEntityLabel={variable => variable.table_code_index || variable.id || ''}
                    getValue={valueObj => valueObj.value ?? null}
                    setValue={(valueObj, newValue) => {
                      const updated = { ...valueObj };
                      updated.value =
                        newValue !== null && newValue !== '' ? Number(newValue) : undefined;
                      return updated as any;
                    }}
                    dateColumnHeader={dateColumnHeader}
                    dateColumnHeaderExpanded={dateColumnHeaderExpanded}
                    renderEntityHeader={renderVariableHeader}
                    renderEntityHeaderExpanded={renderVariableHeaderExpanded}
                    entityColumnWidth={120}
                    entityColumnWidthExpanded={200}
                    columnOrder={columnOrderFields}
                    onColumnOrderChange={handleColumnOrderChange}
                    onCellEditStop={handleCellEditStop}
                    readOnly={readOnly}
                    height="calc(100vh - 380px)"
                    loading={
                      isLoading ||
                      isLoadingVariablesData ||
                      batchSaveMutation.isLoading ||
                      batchSaveVariablesData.isLoading ||
                      updateOrderMutation.isLoading
                    }
                    enablePaste={true}
                    syncScrollLeft={horizontalScrollLeft}
                    onHorizontalScroll={setScrollPos}
                  />
                </Box>
                <Typography variant="subtitle1">
                  Dati di consumo energetico, di produzione e di costo - valori annui
                </Typography>
                <Box sx={{ width: '100%' }}>
                  <CrossEntityDateCrudDataGrid
                    ref={crossEntityGridRefAnnual}
                    granularity="year"
                    periodFrom={diagnosis.data_generated_from!}
                    periodTo={diagnosis.data_generated_to!}
                    entities={sortedVariables}
                    values={variableYearlyValues}
                    valuesProps={variableYearlyValuesProps}
                    getEntityLabel={variable => variable.table_code_index || variable.id || ''}
                    dateColumnHeader="Anno"
                    entityColumnWidth={120}
                    entityColumnWidthExpanded={200}
                    columnOrder={columnOrderFields}
                    onColumnOrderChange={handleColumnOrderChange}
                    renderEntityHeader={renderVariableHeader}
                    height={185}
                    loading={
                      isLoading ||
                      isLoadingVariablesData ||
                      batchSaveMutation.isLoading ||
                      batchSaveVariablesData.isLoading ||
                      updateOrderMutation.isLoading
                    }
                    readOnly={true}
                    disableHeaderExpand={true}
                    syncScrollLeft={horizontalScrollLeft}
                    onHorizontalScroll={setScrollPos}
                  />
                </Box>
                <Typography variant="subtitle1">
                  Periodo dati generati per la diagnosi energetica
                </Typography>
                <Box sx={{ width: '100%' }}>
                  <CrossEntityDateCrudDataGrid
                    ref={crossEntityGridRefPeriodoRiferimento}
                    granularity="year"
                    periodFrom={diagnosis.reference_period_from ?? diagnosis.data_generated_from!}
                    periodTo={diagnosis.reference_period_to ?? diagnosis.data_generated_to!}
                    entities={sortedVariables}
                    values={variableReferencePeriodYearlyValues}
                    valuesProps={variableYearlyValuesProps}
                    getEntityLabel={variable => variable.table_code_index || variable.id || ''}
                    dateColumnHeader="Anno"
                    entityColumnWidth={120}
                    entityColumnWidthExpanded={200}
                    columnOrder={columnOrderFields}
                    onColumnOrderChange={handleColumnOrderChange}
                    renderEntityHeader={renderVariableHeader}
                    height={85}
                    loading={
                      isLoading ||
                      isLoadingVariablesData ||
                      batchSaveMutation.isLoading ||
                      batchSaveVariablesData.isLoading ||
                      updateOrderMutation.isLoading
                    }
                    readOnly={true}
                    disableHeaderExpand={true}
                    syncScrollLeft={horizontalScrollLeft}
                    onHorizontalScroll={setScrollPos}
                  />
                </Box>
              </Stack>
            )}
            <ActionSpacer></ActionSpacer>
          </Stack>

          <EditRelevantVariableDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedVariable(null);
            }}
            variable={selectedVariable}
            diagnosisId={diagnosisId}
            existingVariables={variables}
          />

          <ConfirmDialog
            open={deleteDialogState.open}
            onCancel={handleCloseDeleteDialog}
            onConfirm={handleConfirmDelete}
            title="Conferma eliminazione"
            content="Sei sicuro di voler eliminare questa variabile pertinente?"
            confirmText="Elimina"
            cancelText="Annulla"
            loading={deleteMutation.isLoading}
          />

          <ColumnMappingDialog
            open={pasteMappingDialog.open}
            onClose={() => setPasteMappingDialog({ open: false, clipboardData: [] })}
            onImport={handleImportWithMapping}
            clipboardData={pasteMappingDialog.clipboardData}
            fields={variableFields}
            dataOrientation="row"
          />
        </>
      );
    }
  )
);

DiagnosisRelevantVariablesStep.displayName = 'DiagnosisRelevantVariablesStep';
